import Vue from 'vue';
import migrateInstance from '@/migrations/instances';
import migrateModal from '@/migrations/modals';
import getPropDefaults from '@/helpers/getPropDefaults';
import isObject from '@/helpers/isObject';
import * as modules from '@/modules';
import globalDataModules from '@/modules/global-data';
import modalModules from '@/modules/modals';
import cloneObject from '@/helpers/cloneObject';
import api from '@/store/helpers/api';

export default {
  state: {
    parentGlobalInstanceList: [],
    globalInstances: {},
    globalData: {},
    activeGlobalData: null,
    activeGlobalDataUnedited: null,
    activeGlobalDataOrigin: null,
  },
  mutations: {
    RESET_GLOBALS(state) {
      state.parentGlobalInstanceList = [];
      state.globalInstances = {};
      state.globalData = {};
    },
    SET_GLOBAL(state, global) {
      let globalToSet = global;

      // Modules made to be merged will always be apart of reusable modules
      // and its name will always start with 'Global'
      // TODO: Change .includes('global') to .startsWith('Global')
      const stateGlobalObject = global.module.includes('Global')
        ? state.globalData
        : state.globalInstances;

      const existingGlobal = stateGlobalObject[global.id];
      if (existingGlobal) {
        globalToSet = { ...existingGlobal, ...global };
        // Push to parent list only once
      } else if (
        Array.isArray(global.translations) &&
        !global.module.includes('Global')
      ) {
        state.parentGlobalInstanceList.push(global.id);
      }

      Vue.set(stateGlobalObject, global.id, globalToSet);
    },
    REMOVE_GLOBAL(state, globalId) {
      Vue.delete(state.globalData, globalId);
      Vue.delete(state.globalInstances, globalId);
    },
    // Global instances mutations
    SET_GLOBAL_INSTANCE_STATUS(state, payload) {
      const { globalId, isPublished } = payload;

      if (state.globalInstances[globalId]) {
        Vue.set(state.globalInstances[globalId], 'isPublished', isPublished);
      }
      if (state.instances[globalId]) {
        Vue.set(state.globalInstances[globalId], 'isPublished', isPublished);
      }
    },
    SET_GLOBAL_INSTANCE_DIRTINESS(state, payload) {
      const { globalId, isDirty } = payload;

      if (state.globalInstances[globalId]) {
        Vue.set(state.globalInstances[globalId], 'isDirty', isDirty);
      }
      if (state.instances[globalId]) {
        Vue.set(state.instances[globalId], 'isDirty', isDirty);
      }
    },
    // Global data mutations
    SET_ACTIVE_GLOBAL_DATA(state, globalId) {
      state.activeGlobalData = globalId;
    },
    SET_ACTIVE_GLOBAL_DATA_UNEDITED(state, globalData) {
      state.activeGlobalDataUnedited = cloneObject(globalData);
    },
    SET_ACTIVE_GLOBAL_DATA_ORIGIN(state, modelPath) {
      state.activeGlobalDataOrigin = modelPath;
    },
  },
  getters: {
    globalInstance: (state) => (globalInstanceId) =>
      state.globalInstances[globalInstanceId],
    globalData: (state) => (globalDataId) => state.globalData[globalDataId],
    activeGlobalData: (state) => state.globalData[state.activeGlobalData],
    // Reduce is not magic. Know how to use it and you'll be able to iterate through objects
    menus: (state, { container: { language } } = {}) =>
      Object.entries(state.globalData).reduce(
        (acc, [key, globalData]) =>
          // If the globalData module is GlobalMenu, insert its
          // value within its key in the final object,
          // the accumulator. If it's not, don't.
          globalData.module === 'GlobalMenu' &&
          (!language || globalData.language === language)
            ? { ...acc, [key]: globalData }
            : acc,
        {},
      ), // Initialize the accumulator as an empty object
  },
  actions: {
    loadGlobals({ commit, dispatch }, { endpoint }) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        api
          .get(endpoint)
          .then(({ data }) => [...data])
          // Multiple filters for readabelity. TODO: Change to reduce
          .then((globals) => [
            globals.filter((doc) => typeof doc.parentId === 'undefined'),
            globals.filter((doc) => typeof doc.parentId === 'string'),
          ])
          .then(([parents, children]) =>
            parents.map((parent) => ({
              ...parent,
              translations: children.filter(
                ({ parentId }) => parentId === parent.id,
              ),
            })),
          )
          .then((globals) => {
            commit('RESET_GLOBALS');
            globals.forEach((global) => {
              dispatch('storeGlobal', { ...global, endpoint });
            });
          })
          .then(resolve)
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    storeGlobal({ commit, dispatch }, { endpoint, ...doc }) {
      const migrate = endpoint === 'globals' ? migrateInstance : migrateModal;
      const { translations, translationIds, ...global } = migrate(doc);

      // Consider that it might have already been treated checking on translationIds
      // Then just remove it from original data by not setting it in the final object
      if (Array.isArray(translations) && !Array.isArray(translationIds)) {
        // TODO: Refact to store translations before adding them to parent
        // So they get merged first with fixed globals at storeGlobal
        const removeDuplicates = (origin) => [...new Set(origin)];

        global.translationIds = removeDuplicates(
          translations.map(({ id }) => id),
        );
        global.translations = removeDuplicates(
          translations.map(({ language }) => language),
        );
      }

      if (global.data && !global.module.includes('Global')) {
        dispatch('storeInstance', global);
      }

      // Consider that it might have already been treated checking on translationIds
      // Then just remove it from original data by not setting it in the final object
      if (Array.isArray(translations) && !Array.isArray(translationIds)) {
        translations.forEach((translation) => {
          dispatch('storeGlobal', { ...translation, endpoint });
        });
      }

      commit('SET_GLOBAL', global);
    },
    loadGlobal(
      { commit, dispatch, getters: { globalInstance, globalData } },
      { id, endpoint },
    ) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        api
          .get(`${endpoint}/${id}`)
          .then((response) => response.data)
          .then((global) => {
            if (isObject(global)) {
              dispatch('storeGlobal', { ...global, endpoint });
            }

            // Use global getter to get fully merged object to resolve
            // because API read method only returns what
            // is missing in API list method
            resolve(globalInstance(id) ?? globalData(id));
          })
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    // Global instance actions
    async addGlobalInstance(
      { commit, dispatch },
      { base: { id, ...base }, endpoint },
    ) {
      let global = cloneObject(base);

      if (global.parentId || id) {
        try {
          const loadedGlobal = await dispatch('loadGlobal', {
            id: global.parentId || id,
            endpoint,
          });

          // Overwrite everything with loaded data, but persists language when creating translations
          global = cloneObject({
            ...global,
            ...loadedGlobal,
            language: global.language,
          });
        } catch (error) {
          console.log(error);
          throw new Error('Failed to load global instance on clone');
        }
      }

      if (id) {
        global.title = `Cópia de ${global.title}`;
      }

      if (!global.data) {
        const moduleList = endpoint === 'globals' ? modules : modalModules;

        global.data = { ...getPropDefaults(moduleList[base.module]) };
      }

      commit('SET_LOADING_STATE', true);

      global.isPublished = false;
      global.isDirty = true;

      const { data: createdGlobal } = await api.post(endpoint, global);

      commit('SET_LOADING_STATE', false);

      return { ...global, ...createdGlobal };
    },
    editGlobalInstanceBasicInfo({ commit, dispatch }, { id, data, endpoint }) {
      return new Promise((resolve) => {
        commit('SET_LOADING_STATE', true);

        api
          .patch(`${endpoint}/${id}`, data)
          .then(() => dispatch('loadGlobals', { endpoint }))
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    removeGlobalInstance(
      { commit, dispatch },
      { id, translationIds, endpoint = 'globals' },
    ) {
      return new Promise((resolve) => {
        commit('SET_LOADING_STATE', true);

        const requests = [api.delete(`${endpoint}/${id}`)];

        if (Array.isArray(translationIds)) {
          translationIds.forEach((translationId) => {
            requests.push(api.delete(`${endpoint}/${translationId}`));
          });
        }

        Promise.all(requests)
          .then(() => dispatch('loadGlobals', { endpoint }))
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    setGlobalInstanceStatus({ commit, getters: { globalInstance } }, payload) {
      return new Promise((resolve) => {
        const {
          global: { id: globalId },
          isPublished,
          endpoint,
        } = payload;

        commit('SET_LOADING_STATE', true);

        api
          .put(`${endpoint}/${globalId}/isPublished`, {
            isPublished,
          })
          .then(() => {
            commit('UPDATE_SAVE_TIME');
            commit('SET_GLOBAL_INSTANCE_STATUS', {
              globalId,
              isPublished,
            });

            commit('SET_GLOBAL_INSTANCE_DIRTINESS', {
              globalId,
              isDirty: !isPublished,
            });

            resolve(globalInstance(globalId));
          })
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    saveActiveGlobalInstance({
      commit,
      dispatch,
      getters: { activeInstance },
    }) {
      const { created, ...instance } = activeInstance;

      commit('SET_LOADING_STATE', true);

      const apiCall = !created
        ? api.post('globals', instance)
        : api.patch(`globals/${instance.id}`, instance);

      apiCall
        .then(() => {
          instance.isDirty = true;

          if (!created) {
            // Add created: true so there's no need to load the
            // global again to get its actual created date
            dispatch('storeGlobal', {
              created: true,
              endpoint: 'globals',
              ...instance,
            });
            dispatch('storeInstance', { created: true, ...instance });
          } else {
            dispatch('storeGlobal', { endpoint: 'globals', ...instance });
          }

          dispatch('validateActiveContainer');
          return { error: null };
        })
        .catch((error) => ({ error }))
        .finally(() => commit('SET_LOADING_STATE', false));
    },
    publishActiveGlobalInstance({
      commit,
      dispatch,
      getters: { activeInstance },
    }) {
      dispatch('setGlobalInstanceStatus', {
        global: activeInstance,
        isPublished: true,
        endpoint: 'globals',
      }).then((globalInstance) => {
        // Update external global instance edition
        dispatch('storeInstance', globalInstance);

        commit('SET_ACTIVE_INSTANCE', null);
      });
    },
    cancelActiveGlobalInstance({
      commit,
      dispatch,
      state: { activeInstance },
      getters: { globalInstance },
    }) {
      dispatch('storeInstance', globalInstance(activeInstance));
      commit('SET_ACTIVE_INSTANCE', null);
    },
    // Global data actions
    editGlobalData({ commit, dispatch }, { globalId, modelPath }) {
      dispatch('loadGlobal', { id: globalId, endpoint: 'globals' }).then(
        (global) => {
          commit('SET_ACTIVE_GLOBAL_DATA', globalId);
          commit('SET_ACTIVE_GLOBAL_DATA_UNEDITED', global);
          if (modelPath) {
            commit('SET_ACTIVE_GLOBAL_DATA_ORIGIN', modelPath);
          }
        },
      );
    },
    async addGlobalData(
      { commit, dispatch, getters },
      { baseGlobalData: { id, ...base }, modelPath },
    ) {
      let globalData = cloneObject(base);

      if (id) {
        try {
          globalData = cloneObject(
            await dispatch('loadGlobal', { id, endpoint: 'globals' }),
          );
        } catch (error) {
          console.log(error);
          console.error('Faled to load global data on clone');
        }
      }

      globalData.id = `Creating${base.module}`;
      globalData.language = getters.container.language || 'pt';

      if (globalData.title) {
        globalData.title = `Cópia de ${base.title}`;
      } else {
        globalData.title = '';
      }

      if (!globalData.data) {
        globalData.data = {
          ...getPropDefaults(globalDataModules[base.module]),
        };
      }

      dispatch('storeGlobal', { endpoint: 'globals', ...globalData });

      commit('SET_ACTIVE_GLOBAL_DATA_UNEDITED', globalData);
      commit('SET_ACTIVE_GLOBAL_DATA', globalData.id);
      if (modelPath) {
        commit('SET_ACTIVE_GLOBAL_DATA_ORIGIN', modelPath);
      }
    },
    deleteGlobalData({ commit }, { globalId }) {
      commit('SET_LOADING_STATE', true);

      return api
        .delete(`globals/${globalId}`)
        .then(() => {
          commit('REMOVE_GLOBAL', globalId);
        })
        .catch((error) => console.log(error))
        .finally(() => commit('SET_LOADING_STATE', false));
    },
    saveActiveGlobalData({
      commit,
      dispatch,
      state: { activeGlobalDataOrigin },
      getters: { activeGlobalData, activeEditableObjectData },
    }) {
      const { id, ...globalData } = activeGlobalData;

      commit('SET_LOADING_STATE', true);

      const apiCall = id.includes('Creating')
        ? api.post('globals', globalData)
        : api.patch(`globals/${id}`, globalData);

      apiCall
        // set income id as globalDataId default so patch
        // endpoint doesn't have to return anything
        .then(({ data: { id: globalDataId = id } }) => {
          commit('SET_ACTIVE_GLOBAL_DATA', null);
          dispatch('storeGlobal', {
            ...globalData,
            endpoint: 'globals',
            id: globalDataId,
          });
          if (id.includes('Creating')) {
            commit('REMOVE_GLOBAL', id);
          }

          if (activeGlobalDataOrigin) {
            const objectData = activeEditableObjectData({
              path: activeGlobalDataOrigin,
            });

            commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
              path: activeGlobalDataOrigin,
              value: { ...objectData, globalReference: globalDataId },
            });
          }

          commit('SET_ACTIVE_GLOBAL_DATA_ORIGIN', null);

          return { error: null };
        })
        .catch((error) => ({ error }))
        .finally(() => commit('SET_LOADING_STATE', false));
    },
    cancelActiveGlobalData({
      commit,
      dispatch,
      state: { activeGlobalData, activeGlobalDataUnedited },
    }) {
      if (activeGlobalData.includes('Creating')) {
        commit('REMOVE_GLOBAL', activeGlobalData);
      } else {
        dispatch('storeGlobal', {
          endpoint: 'globals',
          ...activeGlobalDataUnedited,
        });
      }

      commit('SET_ACTIVE_GLOBAL_DATA', null);
      commit('SET_ACTIVE_GLOBAL_DATA_ORIGIN', null);
    },
    // Both globals' actions. Used in instance properties.
    // Which is temporary to global instances
    saveActiveGlobal({ dispatch, state: { activeGlobalData } }) {
      if (activeGlobalData) {
        dispatch('saveActiveGlobalData');
      } else {
        dispatch('saveActiveGlobalInstance');
      }
    },
    cancelActiveGlobal({ dispatch, state: { activeGlobalData } }) {
      if (activeGlobalData) {
        dispatch('cancelActiveGlobalData');
      } else {
        dispatch('cancelActiveGlobalInstance');
      }
    },
  },
};
